import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

interface ChatBubbleProps {
  /**
   * "customer" = the owner roleplaying a customer (right-aligned, IG-style
   * gradient); "bot" = the assistant reply (left-aligned, light gray). The
   * naming mirrors production semantics — the owner tests how the bot replies
   * to *customers*, not to themselves.
   */
  role: 'customer' | 'bot';
  /** Omitted when `pending` is true. */
  text?: string;
  /** Renders the 3-dot typing animation instead of text. */
  pending?: boolean;
}

/**
 * Pure presentational chat bubble for the F026 preview surface. Bubble color /
 * alignment / radius are driven by `role` reading the scoped IG-mimic tokens
 * (see `preview-chat.tokens.css`). The asymmetric border-radius (one bottom
 * corner reduced to 0.25rem) is the IG "tail" effect achieved purely with CSS
 * radius — no SVG tail. Intentionally NOT shared with the F022 outbox UI.
 */
export function ChatBubble({ role, text, pending = false }: ChatBubbleProps) {
  const { t } = useTranslation();
  const isCustomer = role === 'customer';

  return (
    <div className={cn('flex', isCustomer ? 'justify-end' : 'justify-start')} data-role={role}>
      <div
        data-testid={`chat-bubble-${role}`}
        className={cn(
          'max-w-[75%] px-3.5 py-2.5 text-sm leading-snug whitespace-pre-wrap break-words',
          'rounded-[1.25rem]',
          isCustomer
            ? 'rounded-br-[0.25rem] text-[color:var(--preview-chat-customer-text)] bg-[image:var(--preview-chat-customer-grad)]'
            : 'rounded-bl-[0.25rem] text-[color:var(--preview-chat-bot-text)] bg-[var(--preview-chat-bot-bg)]'
        )}
      >
        {pending ? (
          <span
            className="flex items-center gap-1 py-0.5"
            role="status"
            aria-label={t('bot.preview.typing')}
            data-testid="typing-indicator"
          >
            <span className="preview-typing-dot" />
            <span className="preview-typing-dot" />
            <span className="preview-typing-dot" />
          </span>
        ) : (
          text
        )}
      </div>
    </div>
  );
}
